import { type Router } from 'next/router';
import { useEffect } from 'react';

import { datadogRum } from '@datadog/browser-rum';

import { Pages } from '@/src/config/pages';

const AlgorithmVersion = 'cpu_quiet_v2';
const DefaultQuietWindowMs = 2500;
const MaximumMeasurementMs = 60000;
const InitialLoadType = 'initial';
const NavigationLoadType = 'navigation';

type LoadType = typeof InitialLoadType | typeof NavigationLoadType;
type UnavailableReason =
  | 'dropped_entries'
  | 'hidden'
  | 'invalid'
  | 'missing_fcp'
  | 'route_change_error'
  | 'superseded'
  | 'timeout'
  | 'unmounted'
  | 'unsupported'
  | 'visibility_history_unsupported';

interface Measurement {
  candidateTime: null | number;
  firstContentfulPaint: null | number;
  hydrationCommitted: null | number;
  lastLongTaskEnd: number;
  loadType: LoadType;
  navigationUrl: null | string;
  routePath: string;
  startTime: number;
  viewTimingEligible: boolean;
}

type PerformanceObserverCallbackWithOptions = (
  entries: PerformanceObserverEntryList,
  observer: PerformanceObserver,
  options?: PerformanceObserverCallbackOptionsWithDrops,
) => void;

interface PerformanceObserverConstructor {
  new (callback: PerformanceObserverCallbackWithOptions): PerformanceObserver;
  readonly supportedEntryTypes?: readonly string[];
}

interface PerformanceObserverCallbackOptionsWithDrops {
  readonly droppedEntriesCount?: number;
}

interface RouteChangeOptions {
  readonly shallow: boolean;
}

interface DestinationRoute {
  readonly browserPathname: string;
  readonly routePath: string;
}

type InitialVisibilityHistory = 'hidden' | 'unsupported' | 'visible';

type TTIRouter = Pick<Router, 'asPath' | 'events' | 'pathname'>;

const getFirstContentfulPaint = (): null | number => {
  const entries = performance.getEntriesByName('first-contentful-paint', 'paint');
  return entries.length === 0 ? null : entries[0].startTime;
};

const getPerformanceObserver = (): null | PerformanceObserverConstructor => {
  const value: unknown = Reflect.get(window, 'PerformanceObserver');
  return typeof value === 'function' ? (value as PerformanceObserverConstructor) : null;
};

const supportsEntryType = (Observer: PerformanceObserverConstructor, entryType: string): boolean =>
  Observer.supportedEntryTypes === undefined || Observer.supportedEntryTypes.includes(entryType);

const getDestinationRoute = (navigationUrl: string): DestinationRoute => {
  try {
    const browserPathname = new URL(navigationUrl, window.location.origin).pathname;
    const page = Pages.get({ path: browserPathname }, { strict: false });
    return {
      browserPathname,
      routePath: page?.path.replace(/:([^/]+)/g, '[$1]') ?? 'unknown',
    };
  } catch {
    return { browserPathname: 'unknown', routePath: 'unknown' };
  }
};

const getBrowserPathname = (url: string): string => {
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return window.location.pathname;
  }
};

const getInitialVisibilityHistory = (
  Observer: null | PerformanceObserverConstructor,
  hydrationCommitted: number,
): InitialVisibilityHistory => {
  if (Observer === null || !supportsEntryType(Observer, 'visibility-state')) {
    return 'unsupported';
  }

  let entries: PerformanceEntry[];
  try {
    entries = performance.getEntriesByType('visibility-state');
  } catch {
    return 'unsupported';
  }

  const initialEntry = entries.find(entry => entry.startTime === 0);
  if (initialEntry?.name !== 'hidden' && initialEntry?.name !== 'visible') {
    return 'unsupported';
  }

  return entries.some(entry => entry.name === 'hidden' && entry.startTime <= hydrationCommitted)
    ? 'hidden'
    : 'visible';
};

/**
 * Measures a low-overhead, CPU-quiet approximation of Time to Interactive for initial document
 * loads and NextJS route changes.
 *
 * This is intentionally not the retired Lighthouse TTI algorithm: it does not monitor network
 * quiet. A view becomes interactive at the later of its readiness floor (FCP and hydration for an
 * initial load, `routeChangeComplete` for a soft navigation) and the latest long-task end, once
 * that candidate has been followed by {@link quietWindowMs} without another long task.
 */
export const useTTI = (router: TTIRouter, quietWindowMs = DefaultQuietWindowMs): void => {
  useEffect(() => {
    let activeMeasurement: Measurement | null = null;
    let longTaskObservationAvailable = false;
    let observer: null | PerformanceObserver = null;
    let paintObservationAvailable = false;
    let timer: null | ReturnType<typeof setTimeout> = null;
    const Observer = getPerformanceObserver();

    const clearTimer = () => {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    };

    const addUnavailableAction = (measurement: Measurement, reason: UnavailableReason): void => {
      datadogRum.addAction('page_tti_unavailable', {
        // eslint-disable-next-line camelcase
        algorithm_version: AlgorithmVersion,
        // eslint-disable-next-line camelcase
        load_type: measurement.loadType,
        // eslint-disable-next-line camelcase
        quiet_window_ms: quietWindowMs,
        reason,
        // eslint-disable-next-line camelcase
        route_path: measurement.routePath,
      });
    };

    const cancelMeasurement = (reason: UnavailableReason): void => {
      clearTimer();
      if (activeMeasurement !== null) {
        addUnavailableAction(activeMeasurement, reason);
        activeMeasurement = null;
      }
    };

    const reportMeasurement = (measurement: Measurement): void => {
      if (measurement.candidateTime === null || activeMeasurement !== measurement) {
        return;
      }
      const tti = measurement.candidateTime - measurement.startTime;
      if (tti < 0 || tti >= MaximumMeasurementMs) {
        cancelMeasurement('invalid');
        return;
      }

      /* Datadog 6.33 accepts this relative duration even though its public API documents an
       * absolute timestamp. Keep the SDK pinned and treat page_tti.tti_ms as authoritative. */
      if (measurement.viewTimingEligible) {
        datadogRum.addTiming('tti', tti);
      }
      datadogRum.addAction('page_tti', {
        // eslint-disable-next-line camelcase
        algorithm_version: AlgorithmVersion,
        // eslint-disable-next-line camelcase
        load_type: measurement.loadType,
        // eslint-disable-next-line camelcase
        quiet_window_ms: quietWindowMs,
        // eslint-disable-next-line camelcase
        route_path: measurement.routePath,
        // eslint-disable-next-line camelcase
        tti_ms: Math.round(tti),
        // eslint-disable-next-line camelcase
        view_timing_emitted: measurement.viewTimingEligible,
      });
      clearTimer();
      activeMeasurement = null;
    };

    const getDeadlineReason = (measurement: Measurement): UnavailableReason =>
      measurement.loadType === InitialLoadType && measurement.firstContentfulPaint === null
        ? 'missing_fcp'
        : 'timeout';

    function processPerformanceEntries(entries: readonly PerformanceEntry[]): void {
      for (const entry of entries) {
        if (entry.entryType === 'longtask') {
          updateForLongTask(entry);
        } else if (entry.entryType === 'paint') {
          updateForPaint(entry);
        }
      }
    }

    const scheduleMeasurement = (measurement: Measurement): void => {
      clearTimer();
      if (activeMeasurement !== measurement) {
        return;
      }

      const now = performance.now();
      const maximumEnd = measurement.startTime + MaximumMeasurementMs;
      if (now >= maximumEnd) {
        cancelMeasurement(getDeadlineReason(measurement));
        return;
      }

      const wakeAt =
        measurement.candidateTime === null
          ? maximumEnd
          : Math.min(measurement.candidateTime + quietWindowMs, maximumEnd);
      timer = setTimeout(
        () => {
          timer = null;
          if (activeMeasurement !== measurement) {
            return;
          }

          processPerformanceEntries(observer?.takeRecords() ?? []);
          if (activeMeasurement !== measurement) {
            return;
          }

          const currentTime = performance.now();
          if (currentTime >= maximumEnd) {
            cancelMeasurement(getDeadlineReason(measurement));
          } else if (
            measurement.candidateTime !== null &&
            currentTime >= measurement.candidateTime + quietWindowMs
          ) {
            reportMeasurement(measurement);
          } else {
            scheduleMeasurement(measurement);
          }
        },
        Math.max(0, wakeAt - now),
      );
    };

    function updateInitialReadiness(measurement: Measurement): void {
      if (
        measurement.loadType !== InitialLoadType ||
        measurement.firstContentfulPaint === null ||
        measurement.hydrationCommitted === null
      ) {
        return;
      }
      measurement.candidateTime = Math.max(
        measurement.firstContentfulPaint,
        measurement.hydrationCommitted,
        measurement.lastLongTaskEnd,
      );
      scheduleMeasurement(measurement);
    }

    function updateForLongTask(entry: PerformanceEntry): void {
      const measurement = activeMeasurement;
      if (measurement === null) {
        return;
      }
      const taskEnd = entry.startTime + entry.duration;
      if (taskEnd <= measurement.startTime || taskEnd <= measurement.lastLongTaskEnd) {
        return;
      }

      measurement.lastLongTaskEnd = taskEnd;
      if (measurement.candidateTime !== null && taskEnd > measurement.candidateTime) {
        measurement.candidateTime = taskEnd;
        scheduleMeasurement(measurement);
      }
    }

    function updateForPaint(entry: PerformanceEntry): void {
      const measurement = activeMeasurement;
      if (measurement?.loadType !== InitialLoadType || entry.name !== 'first-contentful-paint') {
        return;
      }
      measurement.firstContentfulPaint = entry.startTime;
      updateInitialReadiness(measurement);
    }

    const beginMeasurement = (measurement: Measurement): boolean => {
      if (activeMeasurement !== null) {
        cancelMeasurement('superseded');
      }
      activeMeasurement = measurement;
      if (document.visibilityState !== 'visible') {
        cancelMeasurement('hidden');
        return false;
      }
      return true;
    };

    const beginInitialLoad = (): void => {
      const hydrationCommitted = performance.now();
      const initialVisibilityHistory = getInitialVisibilityHistory(Observer, hydrationCommitted);
      const measurement: Measurement = {
        candidateTime: null,
        firstContentfulPaint: getFirstContentfulPaint(),
        hydrationCommitted,
        lastLongTaskEnd: 0,
        loadType: InitialLoadType,
        navigationUrl: null,
        routePath: router.pathname,
        startTime: 0,
        viewTimingEligible: true,
      };
      if (!beginMeasurement(measurement)) {
        return;
      }
      if (initialVisibilityHistory === 'hidden') {
        cancelMeasurement('hidden');
      } else if (!longTaskObservationAvailable) {
        cancelMeasurement('unsupported');
      } else if (initialVisibilityHistory === 'unsupported') {
        cancelMeasurement('visibility_history_unsupported');
      } else if (measurement.firstContentfulPaint === null && !paintObservationAvailable) {
        cancelMeasurement('missing_fcp');
      } else {
        updateInitialReadiness(measurement);
        if (measurement.candidateTime === null) {
          scheduleMeasurement(measurement);
        }
      }
    };

    const handleRouteChangeStart = (url: string, options: RouteChangeOptions): void => {
      if (options.shallow) {
        return;
      }
      const startTime = performance.now();
      const destination = getDestinationRoute(url);
      const measurement: Measurement = {
        candidateTime: null,
        firstContentfulPaint: null,
        hydrationCommitted: null,
        lastLongTaskEnd: startTime,
        loadType: NavigationLoadType,
        navigationUrl: url,
        routePath: destination.routePath,
        startTime,
        viewTimingEligible:
          destination.browserPathname !== 'unknown' &&
          destination.browserPathname !== getBrowserPathname(router.asPath),
      };
      if (beginMeasurement(measurement)) {
        scheduleMeasurement(measurement);
      }
    };

    const handleRouteChangeComplete = (url: string, options: RouteChangeOptions): void => {
      if (options.shallow) {
        return;
      }
      const measurement = activeMeasurement;
      if (measurement?.loadType !== NavigationLoadType || measurement.navigationUrl !== url) {
        return;
      }
      measurement.routePath = router.pathname;
      if (!longTaskObservationAvailable) {
        cancelMeasurement('unsupported');
        return;
      }
      measurement.candidateTime = Math.max(performance.now(), measurement.lastLongTaskEnd);
      scheduleMeasurement(measurement);
    };

    const handleRouteChangeError = (
      _error: unknown,
      url: string,
      options: RouteChangeOptions,
    ): void => {
      if (options.shallow) {
        return;
      }
      if (activeMeasurement?.navigationUrl === url) {
        cancelMeasurement('route_change_error');
      }
    };

    const handleVisibilityChange = (): void => {
      if (document.visibilityState !== 'visible' && activeMeasurement !== null) {
        cancelMeasurement('hidden');
      }
    };

    if (Observer !== null && supportsEntryType(Observer, 'longtask')) {
      observer = new Observer((entries, _observer, options) => {
        if ((options?.droppedEntriesCount ?? 0) > 0 && activeMeasurement !== null) {
          cancelMeasurement('dropped_entries');
          return;
        }
        processPerformanceEntries(entries.getEntries());
      });
      try {
        observer.observe({ buffered: true, type: 'longtask' });
        longTaskObservationAvailable = true;
      } catch {
        observer.disconnect();
        observer = null;
      }

      if (
        observer !== null &&
        getFirstContentfulPaint() === null &&
        supportsEntryType(Observer, 'paint')
      ) {
        try {
          observer.observe({ buffered: true, type: 'paint' });
          paintObservationAvailable = true;
        } catch {
          paintObservationAvailable = false;
        }
      }
    }

    router.events.on('routeChangeStart', handleRouteChangeStart);
    router.events.on('routeChangeComplete', handleRouteChangeComplete);
    router.events.on('routeChangeError', handleRouteChangeError);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    beginInitialLoad();

    return () => {
      if (activeMeasurement !== null) {
        cancelMeasurement('unmounted');
      }
      clearTimer();
      observer?.disconnect();
      router.events.off('routeChangeStart', handleRouteChangeStart);
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
      router.events.off('routeChangeError', handleRouteChangeError);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [quietWindowMs, router]);
};
